import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { Portal } from "@/components/Portal";
import { X, Package, Sparkles, Building2, Users, Gauge, Layers, ChefHat } from "lucide-react";

// Quantity add-on items → the numeric tier limit they raise + an icon.
const QTY_ITEMS = [
  { key: "sites", label: "Additional Sites", limit: "max_sites", icon: Building2 },
  { key: "users", label: "Additional Users", limit: "max_users", icon: Users },
  { key: "equipment", label: "Additional Equipment / site", limit: "max_ccps_per_site", icon: Gauge },
  { key: "facilities", label: "Additional Facilities / site", limit: "max_facilities_per_site", icon: Layers },
];
const ALL_KEYS = [...QTY_ITEMS.map((i) => i.key), "daily_kitchen_record"];
// Backend tier-limit defaults (mirror core/pricing.py + schemas.py) so the
// preview matches what the server computes when a tier omits a limit.
const DEFAULT_LIMITS = { max_sites: 1, max_users: 5, max_ccps_per_site: 5, max_facilities_per_site: 5 };

function fmtSGD(n) {
  return new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD" }).format(Number(n || 0));
}

// Text input that shows a formatted SGD value when blurred, and the raw
// editable number while focused (so typing stays natural).
function CurrencyInput({ value, onChange, testid, style, disabled }) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState("");
  return (
    <input
      className="vb-input" type="text" inputMode="decimal"
      data-testid={testid} disabled={disabled} style={style}
      value={focused ? raw : fmtSGD(value || 0)}
      onFocus={() => { setFocused(true); setRaw(value === "" || value == null ? "" : String(value)); }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const cleaned = e.target.value.replace(/[^0-9.]/g, "");
        setRaw(cleaned);
        onChange(cleaned);
      }}
    />
  );
}

export default function TenantPackageModal({ tenant, tiers, onClose, onChanged }) {
  const { notify } = useDialog();
  const [pricing, setPricing] = useState(null);   // { prices: {item:{monthly,yearly}} }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [tierId, setTierId] = useState(tenant.tier_id || tiers[0]?.id || "");
  const [interval, setInterval] = useState("yearly");
  const [basePrice, setBasePrice] = useState(0);
  const [qty, setQty] = useState({ sites: 0, users: 0, equipment: 0, facilities: 0 });
  const [unit, setUnit] = useState({});
  const [dkr, setDkr] = useState(false);
  const [showAddons, setShowAddons] = useState(false);
  const [locked, setLocked] = useState(0);
  const [lockEdited, setLockEdited] = useState(false);
  const [notes, setNotes] = useState("");

  const tier = useMemo(() => tiers.find((t) => t.id === tierId), [tiers, tierId]);
  const tierBase = (t, iv) => Number(t?.price_monthly || 0) * (iv === "yearly" ? 12 : 1);
  const catalogUnit = (pr, iv) => Object.fromEntries(ALL_KEYS.map((k) => [k, Number(pr?.prices?.[k]?.[iv] ?? 0)]));

  useEffect(() => {
    (async () => {
      try {
        const [pr, pk] = await Promise.all([
          api.get("/admin/addon-pricing"),
          api.get(`/admin/tenants/${tenant.id}/package`),
        ]);
        setPricing(pr.data.pricing);
        const pkg = pk.data.package;
        if (pkg) {
          const iv = pkg.billing_interval || "yearly";
          setTierId(pkg.tier_id);
          setInterval(iv);
          setBasePrice(pkg.base_price ?? 0);
          const q = { sites: 0, users: 0, equipment: 0, facilities: 0 };
          const u = catalogUnit(pr.data.pricing, iv);
          (pkg.addons || []).forEach((a) => {
            if (a.item in q) q[a.item] = a.quantity;
            u[a.item] = a.unit_price;
          });
          setQty(q); setUnit(u); setDkr(!!pkg.daily_kitchen_record);
          setShowAddons(Object.values(q).some((v) => v > 0) || !!pkg.daily_kitchen_record);
          setLocked(pkg.locked_price ?? 0); setLockEdited(true);
          setNotes(pkg.notes || "");
        } else {
          const iv = "yearly";
          const t = tiers.find((x) => x.id === (tenant.tier_id || tiers[0]?.id));
          setBasePrice(tierBase(t, iv));
          setUnit(catalogUnit(pr.data.pricing, iv));
        }
      } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load"); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  // When interval changes, reset unit prices + base to that interval's defaults.
  const changeInterval = (iv) => {
    setInterval(iv);
    setUnit(catalogUnit(pricing, iv));
    setBasePrice(tierBase(tier, iv));
  };
  const changeTier = (id) => {
    setTierId(id);
    setBasePrice(tierBase(tiers.find((t) => t.id === id), interval));
  };

  const addonTotal =
    QTY_ITEMS.reduce((s, it) => s + (Number(qty[it.key]) || 0) * (Number(unit[it.key]) || 0), 0) +
    (dkr ? Number(unit.daily_kitchen_record || 0) : 0);
  const computed = Number(basePrice || 0) + addonTotal;

  useEffect(() => {
    if (!lockEdited) setLocked(Number(computed.toFixed(2)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed, lockEdited]);

  const effLimit = (limitKey, itemKey) => {
    const b = Number(tier?.features?.[limitKey] ?? DEFAULT_LIMITS[limitKey] ?? 0);
    return b + (Number(qty[itemKey]) || 0);
  };

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      const addons = [
        ...QTY_ITEMS.map((it) => ({ item: it.key, quantity: Number(qty[it.key]) || 0, unit_price: Number(unit[it.key]) || 0 })),
        { item: "daily_kitchen_record", quantity: dkr ? 1 : 0, unit_price: Number(unit.daily_kitchen_record) || 0 },
      ];
      await api.put(`/admin/tenants/${tenant.id}/package`, {
        tier_id: tierId, billing_interval: interval, base_price: Number(basePrice) || 0,
        locked_price: Number(locked) || 0, notes, addons,
      });
      await notify({ title: "Package saved", message: `Locked at ${fmtSGD(locked)} / ${interval}.`, variant: "ok" });
      onChanged?.(); onClose();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const perLabel = interval === "yearly" ? "/ year" : "/ month";

  return (
    <Portal><div className="vb-modal-bg" onClick={onClose} data-testid="package-modal-bg">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 780 }} data-testid="package-modal">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Package size={20} style={{ color: "var(--vb-text-2)" }} />
            <div>
              <h2 className="vb-heading text-xl m-0">Package & Pricing · {tenant.company_name}</h2>
              <p className="text-xs opacity-60 m-0">Tier + priced add-ons. Purchased extras raise this customer's limits.</p>
            </div>
          </div>
          <button className="vb-btn ghost" onClick={onClose} data-testid="package-close"><X size={16} /></button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm opacity-60">Loading package…</div>
        ) : (
          <div className="space-y-4">
            {err && <div className="vb-pill err" data-testid="package-error">{err}</div>}

            {/* Tier + interval */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <FieldLabel>Base subscription tier</FieldLabel>
                <select className="vb-input" value={tierId} onChange={(e) => changeTier(e.target.value)} data-testid="package-tier">
                  {tiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel>Billing interval</FieldLabel>
                <div className="flex gap-2">
                  {["monthly", "yearly"].map((iv) => (
                    <button key={iv} type="button"
                      className={`vb-btn ${interval === iv ? "" : "ghost"} flex-1 text-sm`}
                      onClick={() => changeInterval(iv)} data-testid={`package-interval-${iv}`}
                      style={{ textTransform: "capitalize" }}>{iv}</button>
                  ))}
                </div>
                <p className="text-[10px] opacity-50 mt-1">Switching re-quotes prices from the catalog.</p>
              </div>
            </div>

            <div>
              <FieldLabel>Base tier price (SGD {perLabel})</FieldLabel>
              <CurrencyInput value={basePrice} onChange={(v) => setBasePrice(v)} testid="package-base-price" />
              <p className="text-[11px] opacity-50 mt-1">
                Tier baseline: {tier?.features?.max_sites ?? DEFAULT_LIMITS.max_sites} sites · {tier?.features?.max_users ?? DEFAULT_LIMITS.max_users} users · {tier?.features?.max_ccps_per_site ?? DEFAULT_LIMITS.max_ccps_per_site} equipment/site · {tier?.features?.max_facilities_per_site ?? DEFAULT_LIMITS.max_facilities_per_site} facilities/site
              </p>
            </div>

            {/* Add-on line items */}
            <div className="vb-card p-4" style={{ background: "var(--vb-bg-2)" }}>
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="package-addons-toggle">
                <input type="checkbox" checked={showAddons}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setShowAddons(on);
                    if (!on) { setQty({ sites: 0, users: 0, equipment: 0, facilities: 0 }); setDkr(false); }
                  }}
                  data-testid="package-addons-checkbox" />
                <strong className="vb-heading text-sm" style={{ color: "var(--vb-text-2)" }}>Add-ons</strong>
                <span className="text-[11px] opacity-50">{showAddons ? "(untick to clear all add-ons)" : "(tick to configure extra sites, users, equipment, facilities & Daily Kitchen Record)"}</span>
              </label>
              {showAddons && (
              <div className="mt-3 space-y-2" data-testid="package-addons-list">
                {QTY_ITEMS.map((it) => {
                  const lineTotal = (Number(qty[it.key]) || 0) * (Number(unit[it.key]) || 0);
                  return (
                    <div key={it.key} className="grid grid-cols-12 gap-2 items-center" data-testid={`package-row-${it.key}`}>
                      <div className="col-span-12 sm:col-span-5 flex items-center gap-2 text-sm">
                        <it.icon size={15} style={{ color: "var(--vb-text-2)" }} />
                        <span>{it.label}</span>
                      </div>
                      <div className="col-span-3 sm:col-span-2">
                        <label className="text-[10px] uppercase opacity-50">Qty</label>
                        <input className="vb-input" type="number" min="0" value={qty[it.key]}
                          onChange={(e) => setQty({ ...qty, [it.key]: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                          data-testid={`package-qty-${it.key}`} />
                      </div>
                      <div className="col-span-4 sm:col-span-3">
                        <label className="text-[10px] uppercase opacity-50">Unit price</label>
                        <input className="vb-input" type="number" min="0" step="0.01" value={unit[it.key] ?? 0}
                          onChange={(e) => setUnit({ ...unit, [it.key]: e.target.value })}
                          data-testid={`package-unit-${it.key}`} />
                      </div>
                      <div className="col-span-5 sm:col-span-2 text-right text-sm font-semibold" data-testid={`package-line-${it.key}`}>
                        {fmtSGD(lineTotal)}
                      </div>
                    </div>
                  );
                })}

                {/* Daily Kitchen Record flag */}
                <div className="grid grid-cols-12 gap-2 items-center pt-2 border-t" style={{ borderColor: "var(--vb-border)" }} data-testid="package-row-dkr">
                  <label className="col-span-12 sm:col-span-5 flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={dkr} onChange={(e) => setDkr(e.target.checked)} data-testid="package-dkr-toggle" />
                    <ChefHat size={15} style={{ color: "var(--vb-text-2)" }} /> Daily Kitchen Record
                  </label>
                  <div className="col-span-3 sm:col-span-2 text-[11px] opacity-50">{dkr ? "Enabled" : "—"}</div>
                  <div className="col-span-4 sm:col-span-3">
                    <label className="text-[10px] uppercase opacity-50">Price</label>
                    <input className="vb-input" type="number" min="0" step="0.01" value={unit.daily_kitchen_record ?? 0}
                      disabled={!dkr} onChange={(e) => setUnit({ ...unit, daily_kitchen_record: e.target.value })}
                      data-testid="package-unit-dkr" />
                  </div>
                  <div className="col-span-5 sm:col-span-2 text-right text-sm font-semibold" data-testid="package-line-dkr">
                    {fmtSGD(dkr ? Number(unit.daily_kitchen_record || 0) : 0)}
                  </div>
                </div>
              </div>
              )}
            </div>

            {/* Effective limits preview */}
            <div className="flex flex-wrap gap-2 text-xs" data-testid="package-effective">
              {QTY_ITEMS.map((it) => (
                <span key={it.key} className="vb-pill" style={{ background: "var(--vb-bg-2)" }}>
                  {it.label.replace("Additional ", "")}: <strong className="ml-1">{effLimit(it.limit, it.key)}</strong>
                </span>
              ))}
            </div>

            {/* Totals + locked price */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
              <div className="text-sm">
                <div className="flex justify-between opacity-70"><span>Base</span><span>{fmtSGD(basePrice)}</span></div>
                <div className="flex justify-between opacity-70"><span>Add-ons</span><span>{fmtSGD(addonTotal)}</span></div>
                <div className="flex justify-between font-semibold mt-1 pt-1 border-t" style={{ borderColor: "var(--vb-border)" }} data-testid="package-computed">
                  <span>Computed total {perLabel}</span><span>{fmtSGD(computed)}</span>
                </div>
              </div>
              <div>
                <FieldLabel>Locked agreed price (SGD {perLabel}) *</FieldLabel>
                <CurrencyInput value={locked} onChange={(v) => { setLocked(v); setLockEdited(true); }}
                  testid="package-locked-price" style={{ fontWeight: 700, fontSize: 16 }} />
                {lockEdited && Number(locked) !== Number(computed.toFixed(2)) && (
                  <button type="button" className="text-[11px] vb-link mt-1 inline-flex items-center gap-1"
                    onClick={() => { setLocked(Number(computed.toFixed(2))); setLockEdited(false); }}
                    data-testid="package-reset-price">
                    <Sparkles size={11} /> Reset to computed ({fmtSGD(computed)})
                  </button>
                )}
              </div>
            </div>

            <div>
              <FieldLabel>Notes</FieldLabel>
              <textarea className="vb-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. negotiated discount, PO reference" data-testid="package-notes" />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button className="vb-btn ghost" onClick={onClose} data-testid="package-cancel">Cancel</button>
              <button className="vb-btn" onClick={save} disabled={busy || !tierId} data-testid="package-save">
                {busy ? "Saving…" : "Save package"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div></Portal>
  );
}

function FieldLabel({ children }) {
  return <label className="block text-[11px] font-semibold mb-1 uppercase tracking-wider opacity-70">{children}</label>;
}
